import { GenieCategories } from "@/types/shared/categories";

export class VisionPrompt {
  public static getVisionAnalysisPrompt(): string {
    const categoryList = GenieCategories.map((cat) => cat.id).join(", ");

    return `
You are an AI assistant that analyzes business images (business cards, flyers, website screenshots, logos, etc.) to extract comprehensive genie setup parameters.

Analyze the image and extract the following information:

CORE PARAMETERS:
1. Genie Name: Extract from business name or create from context
2. Use Case: Infer the primary business purpose/service
3. Language: Detect language in image (default: en)
4. Voice Preference: Infer appropriate voice characteristics based on business type (professional, friendly, calm, energetic, etc.)
5. System Prompt: Generate an appropriate system prompt based on the inferred business purpose and brand personality
6. First Message: Generate an appropriate greeting message for voice conversations
7. Web Welcome Message: Always format as "Hey! I'm your [genie name]"

BUSINESS CONTEXT:
8. Brand: Extract company/brand name from image
9. Category: What category does this business fall into? Must be one of: ${categoryList}
10. Description: Describe what this business does based on image content
11. Support Emails: Extract any email addresses visible in the image
12. Customer Email: Extract any email addresses that might be for the account owner

VISUAL BRANDING:
13. Colors: Extract primary and secondary brand colors from the image (hex format)
14. Logo/Image URL: Will be provided separately, note if a logo is clearly visible

TECHNICAL PREFERENCES (infer from business type):
15. Temperature: Response creativity level (0-1, default: 0.7) - infer from business personality
16. Max Tokens: Response length preference (default: 8000)

VOICE PARAMETERS (infer from business personality):
17. Voice Stability: How consistent the voice should be (0-1, default: 0.5)
18. Voice Similarity: How similar to original voice (0-1, default: 0.8)
19. Speech Speed: How fast to speak (0.5-2.0, default: 1.0)

CONVERSATION SETTINGS:
20. Max Duration: Maximum conversation length in seconds (default: 600)
21. Turn Timeout: How long to wait for user input (default: 7)
22. Silence Timeout: How long to wait in silence before ending (default: 30)

GOALS:
23. Goal: Infer what conversation objectives this business would want to capture (e.g., "Collect customer contact information", "Answer product questions", "Schedule appointments")

_______________________________________________________________________________________________________

Return a comprehensive JSON object with all extracted parameters. If information cannot be determined from the image, provide reasonable defaults based on the business type.
Use a confidence score (0-1) to indicate how confident you are in the overall extraction.

CRITICAL: The parameter structure and key names must be followed EXACTLY as shown in the example below. Do not modify, rename, or omit any keys. The response must be valid JSON that matches this structure precisely.

Example response:
{
  "genieName": "Acme Support Bot",
  "useCase": "Customer support for tech products",
  "language": "en",
  "voicePreference": "professional helpful calm",
  "systemPrompt": "You are a helpful customer support assistant for Acme Technologies...",
  "firstMessage": "Hi! I'm here to help with any questions about Acme products.",
  "webWelcomeMessage": "Hey! I'm your Acme Support Bot",
  "conversationSettings": {
    "maxDuration": 1800
    "turnTimeout": 7,
    "silenceTimeout": 300,
    "turnMode": "silence"
  },
  "customerEmail": "contact@acme.com",
  "brand": "Acme Technologies",
  "category": "support",
  "description": "Technology company providing innovative solutions for businesses",
  "supportEmails": ["support@acme.com"],
  "sendConversationReports": true,
  "llmModel": "gemini-3-flash-preview",
  "temperature": 0.7,
  "maxTokens": 8000,
  "voiceStability": 0.6,
  "voiceSimilarity": 0.8,
  "speechSpeed": 1.0,
  "goal": "Answer customer questions about products and collect feedback",
  "extractedColors": {
    "primary": "#3B82F6",
    "secondary": "#10B981"
  },
  "confidence": 0.85
}
`;
  }
}
